import { useMemo } from 'react';

import type { MappingResult } from '../types';

/**
 * Extended type for flattened final results rows with rowId
 */
export type FlattenedFinalResultRow = MappingResult & { rowId: string };

/**
 * Custom hook to flatten mapping results for the final results table
 *
 * This hook transforms the hierarchical mapping results (with collapsible Source/Mapped structure)
 * into a flat array of rows suitable for a simple table display.
 *
 * For rows with mapped children (isCollapsible=true), it expands them into separate rows,
 * one for each mapped result. This gives a cleaner view of all final mappings.
 *
 * DEDUPLICATION:
 * - Rows with the same unique key are deduplicated
 * - Uses code+vocabulary as the unique key for all rows
 * - If a row exists as both "Source" and "Mapped", they are merged into a single row with type "Both"
 *
 * FILTERING:
 * - Excludes all Source rows with status "Not found" from the final concept list
 *
 * FEATURES:
 * - Flattens collapsible Source rows with their Mapped children
 * - Deduplicates rows by code+vocabulary unique key
 * - Merges Source/Mapped pairs into "Both"
 * - Filters out Source rows with "Not found" status
 * - Generates unique rowIds for each row based on code+vocabulary
 * - Preserves all original data properties
 * - Memoized for performance
 *
 * USAGE:
 * ```tsx
 * const flatRows = useFlattenedFinalResults(results);
 * <DataGrid rows={flatRows} columns={columns} getRowId={(row) => row.rowId} ... />
 * ```
 *
 * EXAMPLE TRANSFORMATION:
 * Input:
 *   - Source: {id: '1', code: 'CODE1', vocabulary: 'SNOMED', name: 'Test', mappedRows: [{id: '2', code: 'CODE2', vocabulary: 'SNOMED', name: 'Mapped1'}, {id: '1', code: 'CODE1', vocabulary: 'SNOMED', name: 'Test'}]}
 * Output:
 *   - Both: {id: '1', code: 'CODE1', vocabulary: 'SNOMED', name: 'Test', type: 'Both', rowId: 'code:CODE1:vocab:SNOMED'}  // Source and Mapped merged
 *   - Mapped: {id: '2', code: 'CODE2', vocabulary: 'SNOMED', name: 'Mapped1', rowId: 'code:CODE2:vocab:SNOMED'}
 */
export const useFlattenedFinalResults = (results: MappingResult[]): FlattenedFinalResultRow[] => {
  return useMemo(() => {
    /**
     * Helper function to get sort order based on status
     * Primary sort by status: Not found (0), Unmapped (1), Mapped (2), Already standard (3)
     */
    const getStatusOrder = (status: string): number => {
      const statusLower = status.toLowerCase();
      if (statusLower === 'not found') return 0;
      if (statusLower === 'unmapped') return 1;
      if (
        statusLower === 'mapped' ||
        statusLower === 'one mapping' ||
        statusLower === 'multiple mappings' ||
        statusLower === 'multiple mapping'
      )
        return 2;
      if (statusLower === 'already standard') return 3;
      return 4; // Unknown status goes to end
    };

    // Filter out "Not found" codes - they should not appear in the Final concept list
    const filteredResults = results.filter(
      (result) => result.status?.toLowerCase() !== 'not found'
    );

    // First, sort the results in the same way as useMappingResultsDataGrid
    // This gives us the Source rows in the correct order
    const sortedResults = [...filteredResults].sort((a, b) => {
      // First sort by status
      const statusOrderA = getStatusOrder(a.status || '');
      const statusOrderB = getStatusOrder(b.status || '');

      if (statusOrderA !== statusOrderB) {
        return statusOrderA - statusOrderB;
      }

      // If status is "Mapped" or similar, sort by number of nested mapped rows
      // Rows with multiple mapped rows (count > 1) should come before those with single mapped row (count === 1)
      if (statusOrderA === 2) {
        // Status is "Mapped"
        const countA = a.isCollapsible && a.mappedRows ? a.mappedRows.length : 0;
        const countB = b.isCollapsible && b.mappedRows ? b.mappedRows.length : 0;

        // Multiple mappings (count > 1) should come before single mappings (count === 1)
        if (countA > 1 && countB === 1) return -1;
        if (countA === 1 && countB > 1) return 1;

        // Within same count, sort by count descending
        return countB - countA;
      }

      return 0;
    });

    /**
     * Generate a unique key for a row based on code and vocabulary.
     * Always uses code+vocabulary as the unique identifier for consistent deduplication.
     * This is used both for deduplication logic and as the rowId for the DataGrid.
     */
    const getUniqueKey = (row: MappingResult): string => {
      return `code:${row.code || ''}:vocab:${row.vocabulary || ''}`;
    };

    // First pass: collect all unique keys to determine which rows appear as both Source and Mapped
    const sourceKeys = new Set<string>();
    const mappedKeys = new Set<string>();

    sortedResults.forEach((result) => {
      sourceKeys.add(getUniqueKey(result));
      if (result.mappedRows) {
        result.mappedRows.forEach((mr) => mappedKeys.add(getUniqueKey(mr)));
      }
    });

    // Now traverse the SORTED results and build a flattened array where Source rows are followed by their Mapped rows
    const flatRows: FlattenedFinalResultRow[] = [];
    const seenKeys = new Set<string>();

    sortedResults.forEach((result) => {
      const resultKey = getUniqueKey(result);

      // Check if this row appears as both Source and Mapped
      const isBoth = sourceKeys.has(resultKey) && mappedKeys.has(resultKey);

      // Add the Source row with type set to "Both" if it appears as both
      if (!seenKeys.has(resultKey)) {
        seenKeys.add(resultKey);
        const rowToAdd: FlattenedFinalResultRow = {
          ...result,
          type: isBoth ? 'Both' : result.type,
          rowId: getUniqueKey(result),
        };
        flatRows.push(rowToAdd);
      }

      // If this row has mapped children, add them immediately after the Source row
      // Sorted alphabetically by name
      if (result.isCollapsible && result.mappedRows && result.mappedRows.length > 0) {
        // Sort mapped rows alphabetically by name
        const sortedMappedRows = [...result.mappedRows].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );

        sortedMappedRows.forEach((mappedRow) => {
          const mappedRowKey = getUniqueKey(mappedRow);
          // Skip if this mapped row key is already added (would be the case for Both)
          if (!seenKeys.has(mappedRowKey)) {
            seenKeys.add(mappedRowKey);
            // Check if this mapped row appears as both Source and Mapped
            const isMappedRowBoth = sourceKeys.has(mappedRowKey) && mappedKeys.has(mappedRowKey);
            flatRows.push({
              ...mappedRow,
              type: isMappedRowBoth ? 'Both' : mappedRow.type,
              rowId: getUniqueKey(mappedRow),
            });
          }
        });
      }
    });

    return flatRows;
  }, [results]);
};
